import { authenticationService } from "@authentication/services/AuthenticationService";

export const feedbackApi = {
  CATEGORIES: {
    BUG_REPORT: "Bug Report",
    FEATURE_REQUEST: "Feature Request",
    IMPROVEMENT: "Improvement",
    QUESTION: "Question",
    COMPLAINT: "Complaint",
    COMPLIMENT: "Compliment",
    OTHER: "Other",
  },

  // Helper function to get CSRF token from cookies
  getCsrfToken: () => {
    const cookies = document.cookie.split(";");
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split("=");
      if (name === "csrftoken") {
        return decodeURIComponent(value);
      }
    }
    return null;
  },

  // Helper function to make requests with CSRF token
  _makeRequestWithCsrf: async (method, endpoint, data = null, options = {}) => {
    const csrfToken = feedbackApi.getCsrfToken();

    const requestOptions = {
      ...options,
      headers: {
        ...options.headers,
        ...(csrfToken && { "X-CSRF-Token": csrfToken }),
      },
    };

    return await authenticationService._makeRequest(
      method,
      endpoint,
      data,
      requestOptions
    );
  },

  getAllFeedback: async (filters = {}) => {
    let endpoint = "/feedback/messages";

    const params = new URLSearchParams();
    if (filters.createdBy) {
      params.append("created_by", filters.createdBy);
    }
    if (filters.category) {
      params.append("category", filters.category);
    }

    if (params.toString()) {
      endpoint += `?${params.toString()}`;
    }

    const response = await feedbackApi._makeRequestWithCsrf("get", endpoint);
    return response.data;
  },

  getCurrentUserFeedback: async () => {
    const currentUser = authenticationService.getCurrentUser();
    if (!currentUser) {
      throw new Error("No current user found. Please log in first.");
    }

    const userId = currentUser.email || currentUser.username || currentUser.id;
    return await feedbackApi.getAllFeedback({ createdBy: userId });
  },

  getFeedbackByUser: async (userId) => {
    if (!userId) {
      throw new Error("User ID is required");
    }
    return await feedbackApi.getAllFeedback({ createdBy: userId });
  },

  createFeedback: async (feedbackData) => {
    if (!feedbackData.message || typeof feedbackData.message !== "string") {
      throw new Error("Message is required and must be a string");
    }

    const payload = {
      message: feedbackData.message.trim(),
    };

    if (feedbackData.category && typeof feedbackData.category === "string") {
      payload.category = feedbackData.category.trim();
    }

    if ("createdBy" in feedbackData) {
      if (feedbackData.createdBy !== null) {
        payload.created_by = feedbackData.createdBy;
      }
    } else {
      const currentUser = authenticationService.getCurrentUser();
      if (currentUser) {
        const userId =
          currentUser.email || currentUser.username || currentUser.id;
        if (userId) {
          payload.created_by = userId;
        }
      }
    }

    const response = await feedbackApi._makeRequestWithCsrf(
      "post",
      "/feedback/messages",
      payload
    );
    return response.data;
  },

  submitFeedback: async (message, options = {}) => {
    const {
      category = null,
      anonymous = false,
      maxLength = 5000,
      createdBy = undefined,
    } = options;

    if (!message || typeof message !== "string") {
      throw new Error("Feedback message is required and must be a string");
    }

    const trimmedMessage = message.trim();
    if (trimmedMessage.length === 0) {
      throw new Error("Feedback message cannot be empty");
    }

    if (trimmedMessage.length > maxLength) {
      throw new Error(
        `Feedback message must be less than ${maxLength} characters`
      );
    }

    if (category && !Object.values(feedbackApi.CATEGORIES).includes(category)) {
      // Silent fail - non-critical operation
    }

    const feedbackData = {
      message: trimmedMessage,
    };

    if (category && category.trim()) {
      feedbackData.category = category.trim();
    }

    // Handle anonymous vs named posting
    if (anonymous) {
      feedbackData.createdBy = null;
    } else if (createdBy !== undefined) {
      feedbackData.createdBy = createdBy;
    }
    // If neither anonymous nor createdBy is specified, let createFeedback handle it

    return await feedbackApi.createFeedback(feedbackData);
  },

  getFeedbackById: async (feedbackId) => {
    if (!feedbackId) {
      throw new Error("Feedback ID is required");
    }

    const response = await feedbackApi._makeRequestWithCsrf(
      "get",
      `/feedback/messages/${feedbackId}`
    );
    return response.data;
  },
  markFeedbackAsRead: async (feedbackId) => {
    if (!feedbackId) {
      throw new Error("Feedback ID is required");
    }

    const response = await feedbackApi._makeRequestWithCsrf(
      "patch",
      `/feedback/messages/${feedbackId}/read`
    );
    return response.data;
  },

  updateFeedback: async (feedbackId, updateData) => {
    if (!feedbackId) {
      throw new Error("Feedback ID is required");
    }

    if (!updateData.message || typeof updateData.message !== "string") {
      throw new Error("Message is required and must be a string");
    }

    const payload = {
      message: updateData.message.trim(),
    };

    if (updateData.category && typeof updateData.category === "string") {
      payload.category = updateData.category.trim();
    }

    const response = await feedbackApi._makeRequestWithCsrf(
      "put",
      `/feedback/messages/${feedbackId}`,
      payload
    );
    return response.data;
  },

  deleteFeedback: async (feedbackId) => {
    if (!feedbackId) {
      throw new Error("Feedback ID is required");
    }

    const response = await feedbackApi._makeRequestWithCsrf(
      "delete",
      `/feedback/messages/${feedbackId}`
    );
    return response.data;
  },

  deleteFeedbackBatch: async (feedbackIds) => {
    if (!Array.isArray(feedbackIds) || feedbackIds.length === 0) {
      throw new Error("Feedback IDs array is required and cannot be empty");
    }

    const response = await feedbackApi._makeRequestWithCsrf(
      "post",
      "/feedback/messages/delete-batch",
      { feedback_ids: feedbackIds }
    );
    return response.data;
  },

  canModifyFeedback: (feedback) => {
    const currentUser = authenticationService.getCurrentUser();
    if (!currentUser || !feedback) return false;

    const userId = currentUser.email || currentUser.username || currentUser.id;
    return feedback.created_by === userId;
  },

  getAvailableCategories: () => {
    return feedbackApi.CATEGORIES;
  },

  isValidCategory: (category) => {
    return Object.values(feedbackApi.CATEGORIES).includes(category);
  },

  formatFeedback: (feedback) => {
    return {
      ...feedback,
      created_at: feedback.created_at
        ? new Date(feedback.created_at).toLocaleString()
        : "Unknown",
      created_by: feedback.created_by || "Anonymous",
      category: feedback.category || "Uncategorized",
      canModify: feedbackApi.canModifyFeedback(feedback),
    };
  },

  getFeedbackStats: async () => {
    const response = await feedbackApi._makeRequestWithCsrf(
      "get",
      "/feedback/stats"
    );
    return response.data;
  },

  searchFeedback: async (query, filters = {}) => {
    const params = new URLSearchParams();
    if (query) {
      params.append("q", query);
    }
    if (filters.category) {
      params.append("category", filters.category);
    }
    if (filters.createdBy) {
      params.append("created_by", filters.createdBy);
    }

    const endpoint = `/feedback/search?${params.toString()}`;

    const response = await feedbackApi._makeRequestWithCsrf("get", endpoint);
    return response.data;
  },

  exportFeedback: async (filters = {}, format = "csv") => {
    const params = new URLSearchParams();
    params.append("format", format);

    if (filters.category) {
      params.append("category", filters.category);
    }
    if (filters.createdBy) {
      params.append("created_by", filters.createdBy);
    }
    if (filters.dateFrom) {
      params.append("date_from", filters.dateFrom);
    }
    if (filters.dateTo) {
      params.append("date_to", filters.dateTo);
    }

    const endpoint = `/feedback/export?${params.toString()}`;

    const response = await feedbackApi._makeRequestWithCsrf(
      "get",
      endpoint,
      null,
      { responseType: "blob" }
    );
    return response.data;
  },
};

export const FeedbackService = {
  ...feedbackApi,

  init: (baseURL = "") => {
    if (baseURL) {
      // Silent fail - non-critical operation
    }
    return null;
  },

  setCurrentUser: (user) => {
    // Silent fail - handled by authenticationService
    return null;
  },

  getCurrentUserId: () => {
    const currentUser = authenticationService.getCurrentUser();
    if (!currentUser) return null;
    return currentUser.email || currentUser.username || currentUser.id;
  },

  testConnection: async () => {
    try {
      await feedbackApi.testConnection();
      return true;
    } catch (error) {
      return false;
    }
  },

  getFeedbackList: async (filters = {}) => {
    const response = await feedbackApi.getAllFeedback(filters);
    return Array.isArray(response) ? response : [];
  },

  deleteFeedbackWithConfirmation: async (feedbackId, confirm = false) => {
    if (
      !confirm &&
      typeof window !== "undefined" &&
      !window.confirm("Are you sure you want to delete this feedback?")
    ) {
      return false;
    }

    await feedbackApi.deleteFeedback(feedbackId);
    return true;
  },
};

export default feedbackApi;
